import { helpers } from '@aps-management/primapp-common';

/*
 * Intention de livraison d'une inscription compétition / événement.
 *
 * Pourquoi elle existe : le widget Payline encaisse AVANT que l'inscription
 * existe. Le client capture (`paylineCaptureWithToken`, qui écrit la ligne de
 * paiement) puis doit appeler `setEntry`. Quand il disparaît entre les deux,
 * l'argent est pris, la ligne de paiement existe sans commande, et personne
 * n'est inscrit.
 *
 * Ce n'est pas une hypothèse : le 05/08/2026, trois paiements du Golf de
 * Saint-Cloud (20, 40 et 80 EUR, référence `TOURNAMENT-2601400041`) n'ont laissé
 * dans les journaux de master QUE leur capture. Aucun `setEntry`, aucun
 * `confirm` derrière. Les golfeurs ont payé et ne sont pas inscrits.
 *
 * Ce que fait cette intention : elle décrit la MARCHANDISE au moment d'ouvrir la
 * session, pour que le serveur puisse reprendre la livraison sans le client.
 * Elle est inerte tant que l'option `deliveryReconciliation` n'est pas posée sur
 * le golf : elle ne fait qu'enregistrer, elle ne déclenche rien.
 *
 * Contraintes imposées par `paymentSession.toDeliveryIntent` de primagolf-master,
 * qui rejette silencieusement une intention non conforme :
 *  - `scope` non vide est obligatoire ;
 *  - aucune clé ne doit évoquer un moyen de paiement (`card|cvv|cvx|iban|token|
 *    secret|password|wallet`), d'où l'absence volontaire de `payments` ;
 *  - 4 000 caractères au maximum une fois sérialisée.
 */

/* Le même identifiant que celui qu'emploiera l'exécuteur de reprise, et que le natif. */
export const DELIVERY_SCOPE_EVENT_ENTRY = 'EVENT_setEntry';

/* Marge sous la limite de master : mieux vaut une intention réduite que rejetée. */
const MAX_INTENT_LENGTH = 3800;

/*
 * Les seules clés d'un joueur qui servent à rejouer l'inscription. C'est la
 * liste de `asyncSetEntry` dans primapp-common, à l'identique : s'en écarter
 * produirait une intention qui ne se rejoue pas.
 */
const PLAYER_KEYS = ['tid', 'email', 'gender', 'lastname', 'firstname', 'licenseNumber', 'tournament', 'events'];

/*
 * Forme réduite, employée quand la forme complète dépasse la limite. Elle ne
 * permet plus de rejouer seule, mais elle dit qui achetait quoi.
 */
const toReducedPlayers = (players) => players.map((p) => ({ tid: p.tid, lastname: p.lastname, firstname: p.firstname }));


/*
 * Intention de livraison d'une réservation de green-fee.
 *
 * Cas très différent de l'inscription : ici la commande EXISTE DÉJÀ quand le
 * paiement s'ouvre. `bookingBook` a bloqué le créneau au golf, la commande est
 * en `booked`, et le golfeur paie ensuite. Il n'y a donc aucune marchandise à
 * décrire, seulement l'objet à confirmer à DÉSIGNER : trois champs, et pas la
 * moindre donnée personnelle.
 *
 * Pourquoi elle sert malgré la référence `BKG-<8 car.>-<horodatage>`, qui semble
 * déjà désigner la commande : ces 8 caractères sont le `time_low` d'un uuid v1,
 * qui reboucle toutes les 429 secondes. 66 062 commandes de la base partagent ce
 * préfixe avec une autre, soit 1,1 % - assez pour qu'un rapprochement automatique
 * rattache l'argent d'un golfeur à la commande d'un autre. L'intention porte
 * l'uuid COMPLET : la désignation devient exacte, et non plus probable.
 *
 * Ce qu'elle ne fait PAS : la référence de session reste ce qu'elle est. Elle ne
 * doit surtout pas devenir métier, c'est ce qui garantit qu'une ligne de paiement
 * n'est adoptée qu'une seule fois (`SalesOrder.findAdoptableWidgetPayment`).
 *
 * Inerte tant que l'option de golf `deliveryReconciliation` n'est pas posée :
 * aujourd'hui elle enregistre, elle ne déclenche rien.
 */
export const DELIVERY_SCOPE_BOOKING = 'BOOKING_confirm';

/**
 * Construit l'intention de livraison d'une réservation, ou `undefined`.
 *
 * Ne jette jamais : une intention est un confort de reprise, jamais une raison
 * d'empêcher un golfeur de payer.
 *
 * @param {object} params
 * @param {string} params.orderId  Identifiant de la commande déjà créée
 * @param {object} params.golf     Golf courant
 * @returns {string|undefined}     JSON prêt à passer à l'ouverture de session
 */
export const buildBookingDeliveryIntent = ({ orderId, golf }) => {
  try {
    if (!orderId || typeof orderId !== 'string') return undefined;
    if (!golf || !golf.id) return undefined;

    return JSON.stringify({ scope: DELIVERY_SCOPE_BOOKING, golfId: golf.id, orderId });
  } catch (err) {
    console.error('[payment] buildBookingDeliveryIntent.failed', err);
    return undefined;
  }
};

/**
 * Construit l'intention de livraison d'une inscription, ou `undefined` si les
 * données manquent.
 *
 * Ne jette jamais : une intention est un confort de reprise, jamais une raison
 * d'empêcher un golfeur de payer.
 *
 * @param {object} params
 * @param {object} params.enter Charge d'inscription de l'écran
 * @param {object} params.golf  Golf courant
 * @returns {string|undefined}  JSON prêt à passer à `paylineGetToken`
 */
export const buildEntryDeliveryIntent = ({ enter, golf }) => {
  try {
    if (!enter || !golf || !golf.id) return undefined;

    const { tournament, events, entrants, comment = null } = enter;
    if (!entrants || !entrants.length) return undefined;

    // Même calcul que `asyncSetEntry` : la date vient du tournoi, sinon du premier
    // événement. Sans elle, l'inscription ne peut pas être rejouée.
    const date = (tournament && tournament.startDate) || (events && events[0] && events[0].startDate);
    if (!date) return undefined;

    const players = entrants.map((entrant) =>
      helpers.object.filterByKeys(
        {
          ...entrant,
          tid: entrant.reference,
          events: (entrant.events || []).filter((e) => e),
        },
        PLAYER_KEYS,
      ),
    );

    const base = { scope: DELIVERY_SCOPE_EVENT_ENTRY, golfId: golf.id, date, comment };

    const complet = JSON.stringify({ ...base, players });
    if (complet.length <= MAX_INTENT_LENGTH) return complet;

    const reduit = JSON.stringify({ ...base, players: toReducedPlayers(players), truncated: true });
    return reduit.length <= MAX_INTENT_LENGTH ? reduit : undefined;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[payment] buildEntryDeliveryIntent.failed', err);
    return undefined;
  }
};

/*
 * Périmètre du rechargement de compte client.
 *
 * Le plus exposé des parcours : le widget encaisse, puis TOUT le reste - crédit
 * du compte dans le PGB du golf, commande, ligne de paiement, mail - dépend de
 * `createAccountWalletOperation`, appelée depuis le navigateur. S'il disparaît
 * entre les deux, il ne reste rien du tout.
 *
 * Deux cas l'ont prouvé au Golf de Maison Blanche : 100 EUR le 15/08/2026 et
 * 200 EUR le 08/08, tous deux régularisés à la main le 18/08 faute d'intention à
 * rejouer.
 */
export const DELIVERY_SCOPE_ACCOUNT_WALLET = 'AW_createOperation';

/**
 * Construit l'intention de livraison d'un rechargement de compte, ou `undefined`.
 *
 * Ne jette jamais : une intention est un confort de reprise, jamais une raison
 * d'empêcher un golfeur de payer.
 *
 * Le palier est filtré sur les MÊMES clés que l'appel réel à `createOperation`,
 * pour que « livré par le serveur » et « livré par le navigateur » partent de la
 * même description. Le serveur ne lui fait de toute façon pas confiance : il
 * recoupe le palier avec la grille réelle du golf et réécrit le montant crédité
 * depuis elle.
 *
 * `accountId` est transporté pour permettre au serveur de REFUSER une intention
 * qui désignerait un autre compte que celui de la session - il ne la suit jamais.
 *
 * @param {object} params
 * @param {object} params.product  Palier choisi (id, name, refilledAmount, paidAmount)
 * @param {object} params.golf     Golf courant
 * @param {object} params.account  Compte à créditer
 * @returns {string|undefined}     JSON prêt à passer à `paylineGetToken`
 */
export const buildAccountWalletDeliveryIntent = ({ product, golf, account }) => {
  try {
    if (!product || !product.id) return undefined;
    if (!golf || !golf.id) return undefined;
    if (!account || !account.id) return undefined;

    return JSON.stringify({
      scope: DELIVERY_SCOPE_ACCOUNT_WALLET,
      golfId: golf.id,
      accountId: account.id,
      product: helpers.object.filterByKeys(product, ['id', 'name', 'refilledAmount', 'paidAmount']),
    });
  } catch (err) {
    console.error('[payment] buildAccountWalletDeliveryIntent.failed', err);
    return undefined;
  }
};

export default {
  buildAccountWalletDeliveryIntent,
  buildBookingDeliveryIntent,
  buildEntryDeliveryIntent,
};
